/**
 * Upcoming, in the directory: announced events near the donor that have not started (GH-800 on
 * iOS, GH-809 here).
 *
 * ## Three rules, each the owner's or the server's
 *
 * - **Signed in only.** The owner's words for build 24: *"Published events that are not live yet
 *   should show up when logged in, ordered by date, in geofence on search results."* The endpoint
 *   is public and identifier-free — a position is never joined to a session — so the rule is this
 *   screen's: `Home` mounts this section only for a remembered donor with a position.
 * - **By date.** The server's order, soonest first. Nothing here re-sorts it.
 * - **Never live.** No LIVE word, no live colour, no pulse. A future event is never on; the row
 *   says when it starts — "Starts {date}", the iOS wording, with no status tag (owner, build 27) —
 *   and opens the event's own page.
 *
 * ## The date tile
 *
 * The iOS row's tile, in the web's Lantern tokens: a month band, the day, the weekday. It is
 * `aria-hidden` — the row's text already says when, in words a screen reader can read in order —
 * so the tile is a glance for sighted readers and costs nobody else a second reading.
 */
import { useEffect, useState } from "react";
import type { UpcomingEvent } from "@givehere/contracts";
import { fetchUpcomingEvents } from "./api.js";

type Position = { latitude: number; longitude: number };

export function UpcomingEvents({ position }: { position: Position }) {
  const [events, setEvents] = useState<readonly UpcomingEvent[]>([]);

  useEffect(() => {
    let live = true;
    fetchUpcomingEvents(position).then(
      // GH-871b: `eventsHidden` (membership on, not a member) arrives as an empty list, so this
      // section is absent; the directory above it carries the one "Events are for members" line.
      (response) => live && setEvents(response.eventsHidden ? [] : response.events),
      // A list that will not load is absent, never an error: the directory above is still true.
      () => live && setEvents([]),
    );
    return () => {
      live = false;
    };
  }, [position]);

  if (events.length === 0) return null;

  return (
    <section className="upcoming" aria-labelledby="upcoming-heading">
      <h2 id="upcoming-heading" className="home__section home__section--next">
        Upcoming
      </h2>
      <ul className="results">
        {events.map((event) => (
          <li key={event.eventId} className="result">
            <a className="result__inner" href={`/e/${encodeURIComponent(event.eventId)}`}>
              <DateTile startsAt={event.startsAt} />
              <span className="result__text">
                <span className="result__name" dir="auto">
                  {event.eventName}
                </span>
                <span className="result__meta">
                  <span dir="auto">{event.organizationDisplayName}</span>
                  {event.locality ? ` · ${event.locality}` : ""}
                </span>
                <span className="result__meta">Starts {when(event.startsAt)}</span>
              </span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Month band, day, weekday — in the reader's own locale and zone. */
export function DateTile({ startsAt }: { startsAt: string }) {
  const at = new Date(startsAt);
  if (Number.isNaN(at.getTime())) return null;
  return (
    <span className="datetile" aria-hidden="true">
      <span className="datetile__month">{at.toLocaleString([], { month: "short" })}</span>
      <span className="datetile__day">{at.toLocaleString([], { day: "numeric" })}</span>
      <span className="datetile__weekday">{at.toLocaleString([], { weekday: "short" })}</span>
    </span>
  );
}

/** "Sat, Oct 4, 7:00 PM" — the iOS row's wording, in the reader's own zone. */
function when(startsAt: string): string {
  const at = new Date(startsAt);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
